import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import debounce from 'lodash/debounce';
// @mui
import { styled } from '@mui/material/styles';
import { Link, Typography, Autocomplete, InputAdornment, Popper } from '@mui/material';
// utils
import axios from '../../../utils/axios';

// components
import Iconify from '../../../components/Iconify';
import InputStyle from '../../../components/InputStyle';
import SearchNotFound from '../../../components/SearchNotFound';

// ----------------------------------------------------------------------

const PopperStyle = styled((props) => <Popper placement="bottom-start" {...props} />)({
  width: '280px !important',
});

// ----------------------------------------------------------------------
Searchbar.propTypes = {
  isChild: PropTypes.bool,
  placeholder: PropTypes.string,
  endpoint: PropTypes.string,
  singleUserUrl: PropTypes.string,
  idField: PropTypes.string,
};

export default function Searchbar({ isChild=true, placeholder, endpoint, singleUserUrl, idField = 'id' }) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);

  const fetchSearchResults = async (value) => {
    try {
      const authToken = JSON.parse(localStorage.getItem(process.env.REACT_APP_ADMIN_AUTH_TOKEN));
      const response = await axios.get(endpoint, {
        params: { q: value },
        headers: {
          'Authorization': `Bearer ${authToken.token}`
        }
      });

      if (response?.data) {
        setSearchResults(response.data.data.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const debouncedFetch = useRef(debounce(fetchSearchResults, 400)).current;

  const handleChangeSearch = (value, reason) => {
    if (reason === 'blur' || reason === 'reset') {
      return;
    }
    const nextValue = value || '';
    setSearchQuery(nextValue);
    if (nextValue) {
      debouncedFetch(nextValue);
    } else {
      debouncedFetch.cancel();
      setSearchResults([]);
    }
  };

  const handleClick = (url) => {
    navigate(url);
  };

  const handleKeyUp = (event) => {
    event.preventDefault();
    if (event.key === 'Enter') {
      const query = (searchQuery || '').trim().toLowerCase();
      if (!query) {
        return;
      }
      if (isChild) {
        navigate(`/users/children/search/${query}`);
      } else {
        navigate(`/users/parent/search/${query}`);
      }
    }
  };

  return (
    <Autocomplete
      size="small"
      autoHighlight
      popupIcon={null}
      PopperComponent={PopperStyle}
      options={searchResults}
      onInputChange={(event, value, reason) => handleChangeSearch(value, reason)}
      getOptionLabel={(user) => user.middle_name ? 
        `${user.first_name} ${user?.last_name} ${user.middle_name}` : 
        `${user.first_name} ${user?.last_name}`
      }
      noOptionsText={<SearchNotFound searchQuery={searchQuery} />}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderInput={(params) => (
        <InputStyle
          {...params}
          stretchStart={200}
          placeholder={placeholder}
          onKeyUp={handleKeyUp}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <InputAdornment position="start">
                <Iconify icon={'eva:search-fill'} sx={{ ml: 1, width: 20, height: 20, color: 'text.disabled' }} />
              </InputAdornment>
            ),
          }}
        />
      )}
      renderOption={(props, user) => {
        const { id, first_name, last_name, middle_name } = user; // eslint-disable-line
        const username = middle_name ? `${first_name} ${last_name} ${middle_name}` : // eslint-disable-line
                          `${first_name} ${last_name}` // eslint-disable-line
        const userId = user[idField] || id;
        const _singleUserUrl = `${singleUserUrl}/${userId}`;

        return (
          <li key={userId || id} {...props}>
            <Link underline="none" onClick={() => handleClick(_singleUserUrl)}>
              <Typography
                component="span"
                variant="subtitle2"
                color="primary"
              >
                {username}
              </Typography>
            </Link>
          </li>
        );
      }}
    />
  );
}
